/**
 * Aba OpenClaw — Versão Pinnada + Templates + Config de Workspace
 * 
 * 5 seções: Versão, Templates, Backups, Config, Histórico
 */
import { useEffect } from 'preact/hooks';
import { signal, computed } from '@preact/signals';
import { api } from '../../api/client';

import { T } from '../../styles/tokens';
import { ListSkeleton, SkeletonBar, Button } from '../../components/ui';
const colors = {
  bg: T.bg, card: T.bgCard, cardBorder: T.bgHover,
  text: T.border, textMuted: T.textSecondary,
  green: T.success, red: T.danger, yellow: T.warning, blue: T.primary, purple: T.purple,
};

// ── Presets de configuração ──
const PRESETS: Record<string, { label: string; emoji: string; desc: string; config: any }> = {
  balanced: {
    label: 'Balanced', emoji: '⚖️', desc: 'Equilíbrio entre contexto e performance',
    config: {
      agents: { defaults: {
        bootstrapMaxChars: 15000,
        contextPruning: {
          mode: 'cache-ttl', ttl: '30m', keepLastAssistants: 3,
          softTrimRatio: 0.6, hardClearRatio: 0.85, minPrunableToolChars: 500,
          softTrim: { maxChars: 8000, headChars: 500, tailChars: 500 },
          hardClear: { enabled: true, placeholder: '[tool result removed to save context]' },
        },
        compaction: {
          mode: 'safeguard', reserveTokensFloor: 40000, maxHistoryShare: 0.7,
          memoryFlush: { enabled: true, softThresholdTokens: 120000 },
        },
        timeoutSeconds: 900,
        heartbeat: { model: 'elon/claude-haiku-4-5' },
        subagents: { archiveAfterMinutes: 30 },
        sandbox: { browser: { enabled: true } },
      }},
      tools: { web: { fetch: { maxChars: 30000 } } },
      session: { maintenance: { mode: 'enforce', pruneDays: 7, maxEntries: 500 } },
    },
  },
  'max-retention': {
    label: 'Max Retention', emoji: '🧠', desc: 'Máxima retenção de contexto',
    config: {
      agents: { defaults: {
        bootstrapMaxChars: 20000,
        contextPruning: {
          mode: 'cache-ttl', ttl: '45m', keepLastAssistants: 3,
          softTrimRatio: 0.7, hardClearRatio: 0.9, minPrunableToolChars: 500,
          softTrim: { maxChars: 50000, headChars: 500, tailChars: 500 },
          hardClear: { enabled: true, placeholder: '[tool result removed to save context]' },
        },
        compaction: {
          mode: 'safeguard', reserveTokensFloor: 30000, maxHistoryShare: 0.8,
          memoryFlush: { enabled: true, softThresholdTokens: 140000 },
        },
        timeoutSeconds: 900,
        heartbeat: { model: 'elon/claude-haiku-4-5' },
        subagents: { archiveAfterMinutes: 60 },
      }},
      tools: { web: { fetch: { maxChars: 50000 } } },
      session: { maintenance: { mode: 'enforce', pruneDays: 14, maxEntries: 1000 } },
    },
  },
  lightweight: {
    label: 'Lightweight', emoji: '🪶', desc: 'Leve e rápido, menor consumo',
    config: {
      agents: { defaults: {
        bootstrapMaxChars: 10000,
        contextPruning: {
          mode: 'cache-ttl', ttl: '15m', keepLastAssistants: 3,
          softTrimRatio: 0.5, hardClearRatio: 0.75, minPrunableToolChars: 500,
          softTrim: { maxChars: 15000, headChars: 500, tailChars: 500 },
          hardClear: { enabled: true, placeholder: '[tool result removed to save context]' },
        },
        compaction: {
          mode: 'safeguard', reserveTokensFloor: 50000, maxHistoryShare: 0.5,
          memoryFlush: { enabled: true, softThresholdTokens: 80000 },
        },
        timeoutSeconds: 300,
        heartbeat: { model: 'elon/claude-haiku-4-5' },
        subagents: { archiveAfterMinutes: 15 },
      }},
      tools: { web: { fetch: { maxChars: 15000 } } },
      session: { maintenance: { mode: 'enforce', pruneDays: 3, maxEntries: 200 } },
    },
  },
  factory: {
    label: 'Factory Reset', emoji: '🏭', desc: 'Volta aos defaults originais do OpenClaw',
    config: {
      agents: { defaults: {
        bootstrapMaxChars: 20000,
        contextPruning: {
          mode: 'cache-ttl', ttl: '5m', keepLastAssistants: 3,
          softTrimRatio: 0.3, hardClearRatio: 0.5, minPrunableToolChars: 50000,
          softTrim: { maxChars: 4000, headChars: 1500, tailChars: 1500 },
          hardClear: { enabled: true, placeholder: '[Old tool result content cleared]' },
        },
        compaction: {
          mode: 'safeguard', reserveTokensFloor: 20000, maxHistoryShare: 0.5,
          memoryFlush: { enabled: false, softThresholdTokens: 4000 },
        },
        timeoutSeconds: 600,
        heartbeat: {},
        subagents: {},
      }},
      tools: { web: { fetch: { maxChars: 50000 } } },
      session: {},
    },
  },
};

/** Deep merge de objetos */
function deepMerge(target: any, source: any): any {
  const result = { ...target };
  for (const key of Object.keys(source)) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key]) && target[key] && typeof target[key] === 'object') {
      result[key] = deepMerge(target[key], source[key]);
    } else {
      result[key] = source[key];
    }
  }
  return result;
}

// ── State ──
const versionData = signal<{ pinned: string; latest: string; upToDate: boolean } | null>(null);
const templates = signal<any[]>([]);
const history = signal<any[]>([]);
const loading = signal(true);
const error = signal('');
const section = signal<'version' | 'templates' | 'backups' | 'config' | 'history'>('version');
const adminBackups = signal<any[]>([]);

// Config state
const workspaces = signal<any[]>([]);
const selectedWs = signal('');
const wsConfig = signal<any>(null);
const configLoading = signal(false);
const configError = signal('');
const configSaving = signal(false);
const configJsonRaw = signal('');
const showRawJson = signal(false);
const configDirty = signal(false);
const allConfigs = signal<Record<string, any>>({});
const allConfigsLoading = signal(false);

// Modais
const showVersionModal = signal(false);
const newVersion = signal('');
const versionNotes = signal('');
const showTemplateModal = signal(false);
const templateName = signal('');
const templateDesc = signal('');
const templateWsId = signal('');
const actionLoading = signal(false);

// ── Fetch ──

async function fetchVersion() {
  try {
    const data = await api.get<any>('/admin/cluster/openclaw/version');
    versionData.value = data;
  } catch (e: any) { error.value = e.message || 'Erro ao carregar versão'; }
}

async function fetchTemplates() {
  try {
    const data = await api.get<any>('/admin/cluster/openclaw/templates');
    templates.value = data.templates || [];
  } catch { /* silencioso */ }
}

async function fetchHistory() {
  try {
    const data = await api.get<any>('/admin/cluster/openclaw/version-history');
    history.value = data.history || [];
  } catch { /* silencioso */ }
}

async function loadAll() {
  loading.value = true;
  await Promise.all([fetchVersion(), fetchTemplates(), fetchHistory()]);
  loading.value = false;
}

async function applyVersion() {
  if (!newVersion.value.trim()) return;
  actionLoading.value = true;
  try {
    await api.put('/admin/cluster/openclaw/version', {
      version: newVersion.value.trim(),
      notes: versionNotes.value.trim() || undefined,
    });
    showVersionModal.value = false;
    newVersion.value = '';
    versionNotes.value = '';
    await Promise.all([fetchVersion(), fetchHistory()]);
  } catch (e: any) {
    alert('Erro: ' + (e.message || 'Falha ao atualizar versão'));
  }
  actionLoading.value = false;
}

async function createTemplate() {
  if (!templateName.value.trim() || !templateWsId.value.trim()) return;
  actionLoading.value = true;
  try {
    await api.post('/admin/cluster/openclaw/templates', {
      name: templateName.value.trim(),
      description: templateDesc.value.trim() || undefined,
      workspaceId: templateWsId.value.trim(),
    });
    showTemplateModal.value = false;
    templateName.value = '';
    templateDesc.value = '';
    templateWsId.value = '';
    await fetchTemplates();
  } catch (e: any) {
    alert('Erro: ' + (e.message || 'Falha ao criar template'));
  }
  actionLoading.value = false;
}

async function fetchAdminBackups() {
  try {
    const data = await api.get<any>('/admin/cluster/openclaw/backups');
    adminBackups.value = data.backups || [];
  } catch { adminBackups.value = []; }
}

// ── Config Functions ──

async function fetchWorkspaces() {
  try {
    const data = await api.get<any>('/admin/cluster/openclaw/workspaces');
    workspaces.value = data.workspaces || [];
    if (!selectedWs.value && workspaces.value.length > 0) {
      selectedWs.value = workspaces.value[0].namespace || ('ws-' + workspaces.value[0].id);
    }
  } catch { /* silencioso */ }
}

async function fetchAllConfigs() {
  if (workspaces.value.length === 0) await fetchWorkspaces();
  allConfigsLoading.value = true;
  const results: Record<string, any> = {};
  await Promise.allSettled(
    workspaces.value.map(async (w: any) => {
      const ns = w.namespace || `ws-${w.id}`;
      try {
        const data = await api.get<any>(`/admin/cluster/openclaw/config/${ns}`);
        results[ns] = data.config || {};
      } catch { results[ns] = null; }
    })
  );
  allConfigs.value = results;
  allConfigsLoading.value = false;
}

async function fetchConfig(ns: string) {
  if (!ns) return;
  configLoading.value = true;
  configError.value = '';
  try {
    const data = await api.get<any>(`/admin/cluster/openclaw/config/${ns}`);
    wsConfig.value = data.config || {};
    configJsonRaw.value = JSON.stringify(data.config || {}, null, 2);
    configDirty.value = false;
  } catch (e: any) {
    configError.value = e.message || 'Erro ao carregar config';
    wsConfig.value = null;
  }
  configLoading.value = false;
}

async function saveConfig(restart = true) {
  if (!selectedWs.value || !wsConfig.value) return;
  configSaving.value = true;
  try {
    await api.put(`/admin/cluster/openclaw/config/${selectedWs.value}`, {
      config: wsConfig.value,
      restart,
    });
    configDirty.value = false;
    alert(restart ? 'Config salva e gateway reiniciado!' : 'Config salva!');
  } catch (e: any) {
    alert('Erro: ' + (e.message || 'Falha ao salvar'));
  }
  configSaving.value = false;
}

function applyPreset(key: string) {
  const preset = PRESETS[key];
  if (!preset) return;
  const current = wsConfig.value || {};
  wsConfig.value = deepMerge(current, preset.config);
  configJsonRaw.value = JSON.stringify(wsConfig.value, null, 2);
  configDirty.value = true;
}

function updateConfigField(path: string, value: any) {
  const cfg = JSON.parse(JSON.stringify(wsConfig.value || {}));
  const parts = path.split('.');
  let obj = cfg;
  for (let i = 0; i < parts.length - 1; i++) {
    if (!obj[parts[i]]) obj[parts[i]] = {};
    obj = obj[parts[i]];
  }
  obj[parts[parts.length - 1]] = value;
  wsConfig.value = cfg;
  configJsonRaw.value = JSON.stringify(cfg, null, 2);
  configDirty.value = true;
}

function getConfigVal(path: string, fallback: any = '') {
  const parts = path.split('.');
  let obj = wsConfig.value;
  for (const p of parts) {
    if (!obj || typeof obj !== 'object') return fallback;
    obj = obj[p];
  }
  return obj ?? fallback;
}

async function deleteAdminBackup(id: string, name: string) {
  if (!confirm(`Deletar backup "${name}"?`)) return;
  try {
    await api.delete(`/admin/cluster/openclaw/backups/${id}`);
    await fetchAdminBackups();
  } catch (e: any) { alert('Erro: ' + (e.message || '')); }
}

async function deleteTemplate(id: string, name: string) {
  if (!confirm(`Deletar template "${name}"?`)) return;
  try {
    await api.delete(`/admin/cluster/openclaw/templates/${id}`);
    await fetchTemplates();
  } catch (e: any) {
    alert('Erro: ' + (e.message || 'Falha ao deletar'));
  }
}

async function cloneTemplate(id: string) {
  const name = prompt('Nome do novo workspace:');
  if (!name) return;
  try {
    const res = await api.post<any>(`/admin/cluster/openclaw/templates/${id}/clone`, { name });
    alert(res.message || 'Clone iniciado!');
  } catch (e: any) {
    alert('Erro: ' + (e.message || 'Falha ao clonar'));
  }
}

// ── Componente ──

export function OpenClawTab() {
  useEffect(() => { loadAll(); }, []);
  
  // Recarregar dados ao trocar de sub-aba
  useEffect(() => {
    if (section.value === 'version') fetchVersion();
    else if (section.value === 'templates') fetchTemplates();
    else if (section.value === 'backups') fetchAdminBackups();
    else if (section.value === 'config') { fetchAllConfigs(); }
    else if (section.value === 'history') fetchHistory();
  }, [section.value]);

  if (loading.value) {
    return <div style={{ textAlign: 'center', padding: 40 }}><ListSkeleton rows={3} /></div>;
  }

  const v = versionData.value;

  return (
    <div>
      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {([
          { key: 'version' as const, label: '🔖 Versão' },
          { key: 'templates' as const, label: 'Templates' },
          { key: 'backups' as const, label: '📸 Backups' },
          { key: 'config' as const, label: '⚙️ Config' },
          { key: 'history' as const, label: '📜 Histórico' },
        ]).map(t => (
          <Button variant="ghost"
            key={t.key}
            onClick={() => section.value = t.key}
            style={{
              padding: '8px 16px',
              background: section.value === t.key ? colors.blue : colors.card,
              color: T.text, border: `1px solid ${colors.cardBorder}`,
              borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13,
            }}
          >
            {t.label}
          </Button>
        ))}
      </div>

      {error.value && (
        <div style={{ background: T.dangerBg, borderRadius: 8, padding: 12, marginBottom: 16, color: colors.red, fontSize: 13 }}>
          {error.value}
        </div>
      )}

      {/* ── Seção Versão ── */}
      {section.value === 'version' && v && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ background: colors.card, borderRadius: 12, padding: 24, border: `1px solid ${colors.cardBorder}` }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: colors.text, marginBottom: 20, margin: '0 0 20px 0' }}>Versão OpenClaw</h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 20 }}>
              {/* Versão Pinnada */}
              <div style={{ background: T.successBg, borderRadius: 10, padding: 16, border: `1px solid ${T.successBgStrong}` }}>
                <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 6 }}>Versão Pinnada</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: colors.green }}>{v.pinned}</div>
              </div>
              
              {/* Última Disponível */}
              <div style={{ background: T.warningBg, borderRadius: 10, padding: 16, border: `1px solid ${T.warningBgStrong}` }}>
                <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 6 }}>Última no NPM</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: colors.yellow }}>{v.latest}</div>
              </div>
              
              {/* Status */}
              <div style={{ 
                background: v.upToDate ? T.successBg : T.warningBg, 
                borderRadius: 10, padding: 16, 
                border: `1px solid ${v.upToDate ? T.successBg : T.warningBg}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: v.upToDate ? colors.green : colors.yellow }}>
                  {v.upToDate ? 'Atualizado' : 'Nova versão disponível'}
                </div>
              </div>
            </div>
            
            <Button variant="ghost"
              onClick={() => { 
                newVersion.value = v.latest !== 'desconhecida' ? v.latest : ''; 
                showVersionModal.value = true; 
              }}
              style={{
                padding: '10px 20px', background: colors.blue, color: T.text,
                border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14,
              }}
            >
              Aplicar Nova Versão
            </Button>
          </div>
        </div>
      )}

      {/* ── Seção Templates ── */}
      {section.value === 'templates' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: colors.text, margin: 0 }}>Templates de Workspace</h3>
            <Button variant="ghost"
              onClick={() => showTemplateModal.value = true}
              style={{
                padding: '8px 16px', background: colors.green, color: T.text,
                border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13,
              }}
            >
              + Criar Template
            </Button>
          </div>

          {templates.value.length === 0 && (
            <div style={{ background: colors.card, borderRadius: 12, padding: 40, border: `1px solid ${colors.cardBorder}`, textAlign: 'center', color: colors.textMuted }}>
              Nenhum template criado ainda
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
            {templates.value.map((t: any) => (
              <div key={t.id} style={{ background: colors.card, borderRadius: 12, padding: 16, border: `1px solid ${colors.cardBorder}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: colors.text }}>{t.name}</div>
                    {t.description && <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>{t.description}</div>}
                  </div>
                  <span style={{ 
                    padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                    background: T.purpleBg, color: colors.purple,
                  }}>
                    {Array.isArray(t.skills) ? t.skills.length : 0} skills
                  </span>
                </div>
                
                <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 12 }}>
                  Criado em {t.created_at ? new Date(t.created_at + 'Z').toLocaleDateString('pt-BR') : '—'}
                  {t.created_by && ` por ${t.created_by}`}
                </div>
                
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="ghost"
                    onClick={() => cloneTemplate(t.id)}
                    style={{ flex: 1, padding: '6px 12px', background: colors.blue, color: T.text, border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                  >
                    Clonar
                  </Button>
                  <Button variant="ghost"
                    onClick={() => deleteTemplate(t.id, t.name)}
                    style={{ padding: '6px 12px', background: T.dangerBg, color: colors.red, border: `1px solid ${T.dangerBgStrong}`, borderRadius: 6, cursor: `pointer`, fontSize: 12, fontWeight: 600 }}
                  >
                    
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Seção Backups ── */}
      {section.value === 'backups' && (
        <div>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: colors.text, marginBottom: 16, margin: '0 0 16px 0' }}>Backups de Workspaces (Todos os Clientes)</h3>
          {adminBackups.value.length === 0 && (
            <div style={{ padding: 20, color: colors.textMuted, textAlign: 'center' }}>Nenhum backup encontrado.</div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {adminBackups.value.map((b: any) => (
              <div key={b.id} style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: colors.text }}>{b.name}</div>
                  <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>
                    WS: {b.workspace_id} · Cliente: {b.customer_id?.slice(0, 8)}... · {(b.skills || []).length} skills · {b.brain_files_count || 0} arquivos · {b.size_bytes ? (b.size_bytes / 1024).toFixed(1) + ' KB' : '—'}
                  </div>
                  <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2 }}>
                    {b.created_at ? new Date(b.created_at + 'Z').toLocaleString('pt-BR') : ''} {b.created_by ? `· por ${b.created_by}` : ''}
                  </div>
                </div>
                <Button variant="ghost"
                  style={{ padding: '4px 12px', fontSize: 12, borderRadius: 6, border: `1px solid ${colors.red}44`, background: `${colors.red}15`, color: colors.red, cursor: 'pointer' }}
                  onClick={() => deleteAdminBackup(b.id, b.name)}
                >Deletar</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Seção Config ── */}
      {section.value === 'config' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* ── VISÃO GERAL: Tabela com todas as configs ── */}
          {!selectedWs.value && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>⚙️ Configurações dos Workspaces</div>
                  <div style={{ fontSize: 12, color: '#8b8fa3', marginTop: 4 }}>Visão geral de todas as configs. Clique em um workspace para editar.</div>
                </div>
                <Button variant="ghost" onClick={() => fetchAllConfigs()}
                  style={{ padding: '8px 16px', background: '#1a1b2e', color: '#e0e0e6', border: '1px solid #2d2f45', borderRadius: 8, cursor: 'pointer', fontSize: 12 }}>
                  🔄 Atualizar
                </Button>
              </div>

              {allConfigsLoading.value && <div style={{ textAlign: 'center', padding: 40 }}><ListSkeleton rows={5} /></div>}

              {!allConfigsLoading.value && workspaces.value.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 12 }}>
                    <thead>
                      <tr>
                        {['Workspace', 'Status', 'HB Model', 'HB Every', 'Timeout', 'Max Conc', 'Sub-agents', 'Browser', 'Streaming', 'DM Policy', 'Group Policy', 'Pruning', 'TTL', 'Soft Trim', 'Compaction', 'Reserve Tokens', 'Bootstrap', 'Web Fetch', 'Sessions'].map(h => (
                          <th key={h} style={{ padding: '10px 8px', textAlign: 'left', color: '#8b8fa3', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #2d2f45', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {workspaces.value.map((w: any) => {
                        const ns = w.namespace || `ws-${w.id}`;
                        const cfg = allConfigs.value[ns];
                        const get = (path: string, fb: any = '—') => {
                          if (!cfg) return fb;
                          const parts = path.split('.');
                          let o = cfg;
                          for (const p of parts) { o = o?.[p]; if (o === undefined) return fb; }
                          return o;
                        };
                        const hasConfig = cfg !== null && cfg !== undefined;
                        const hbModel = get('agents.defaults.heartbeat.model', '');
                        const timeout = get('agents.defaults.timeoutSeconds', '—');
                        const pruneMode = get('agents.defaults.contextPruning.mode', '—');
                        const pruneTtl = get('agents.defaults.contextPruning.ttl', '—');
                        const softTrim = get('agents.defaults.contextPruning.softTrim.maxChars', '—');
                        const compactMode = get('agents.defaults.compaction.mode', '—');
                        const reserveFloor = get('agents.defaults.compaction.reserveTokensFloor', '—');
                        const bootstrap = get('agents.defaults.bootstrapMaxChars', '—');
                        const webFetch = get('tools.web.fetch.maxChars', '—');
                        const sessMode = get('session.maintenance.mode', '—');

                        const statusColor = w.status === 'running' ? '#22c55e' : w.status === 'stopped' ? '#ef4444' : '#f59e0b';
                        const pill = (val: any, color?: string) => (
                          <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 500, background: color ? `${color}18` : '#1a1b2e', color: color || '#c8cbdb', whiteSpace: 'nowrap' }}>{String(val)}</span>
                        );

                        return (
                          <tr key={w.id}
                            onClick={() => { selectedWs.value = ns; fetchConfig(ns); }}
                            style={{ cursor: 'pointer', transition: 'background 0.15s' }}
                            onMouseEnter={(e) => (e.currentTarget as HTMLElement).style.background = '#1a1b2e'}
                            onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.background = 'transparent'}
                          >
                            <td style={{ padding: '10px 8px', borderBottom: '1px solid #1a1b2e', color: '#fff', fontWeight: 600, whiteSpace: 'nowrap' }}>
                              {w.name || w.id}
                            </td>
                            <td style={{ padding: '10px 8px', borderBottom: '1px solid #1a1b2e' }}>
                              {pill(w.status, statusColor)}
                            </td>
                            <td style={{ padding: '10px 8px', borderBottom: '1px solid #1a1b2e', color: '#c8cbdb', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {!hasConfig ? pill('Erro', '#ef4444') : hbModel ? <span title={hbModel}>{hbModel.split('/').pop()}</span> : pill('Não definido', '#f59e0b')}
                            </td>
                            <td style={{ padding: '10px 8px', borderBottom: '1px solid #1a1b2e', color: '#c8cbdb' }}>
                              {get('agents.defaults.heartbeat.every', '—')}
                            </td>
                            <td style={{ padding: '10px 8px', borderBottom: '1px solid #1a1b2e', color: '#c8cbdb' }}>
                              {timeout !== '—' ? `${timeout}s` : '—'}
                            </td>
                            <td style={{ padding: '10px 8px', borderBottom: '1px solid #1a1b2e', color: '#c8cbdb' }}>
                              {get('agents.defaults.maxConcurrent', '—')}
                            </td>
                            <td style={{ padding: '10px 8px', borderBottom: '1px solid #1a1b2e', color: '#c8cbdb' }}>
                              {get('agents.defaults.subagents.maxConcurrent', '—')}
                            </td>
                            <td style={{ padding: '10px 8px', borderBottom: '1px solid #1a1b2e' }}>
                              {(() => {
                                const browserEnabled = get('agents.defaults.sandbox.browser.enabled', false);
                                return pill(browserEnabled ? '✅ On' : '❌ Off', browserEnabled ? '#22c55e' : '#ef4444');
                              })()}
                            </td>
                            <td style={{ padding: '10px 8px', borderBottom: '1px solid #1a1b2e' }}>
                              {(() => {
                                const streaming = get('channels.telegram.streaming', '—');
                                const color = streaming === 'partial' ? '#f59e0b' : streaming === 'full' ? '#22c55e' : streaming === 'off' ? '#ef4444' : undefined;
                                return pill(streaming === '—' ? '—' : streaming, color);
                              })()}
                            </td>
                            <td style={{ padding: '10px 8px', borderBottom: '1px solid #1a1b2e' }}>
                              {(() => {
                                const dmP = get('channels.telegram.dmPolicy', '—');
                                const color = dmP === 'open' ? '#22c55e' : dmP === 'deny' ? '#ef4444' : dmP === 'allowlist' ? '#f59e0b' : undefined;
                                return pill(dmP, color);
                              })()}
                            </td>
                            <td style={{ padding: '10px 8px', borderBottom: '1px solid #1a1b2e' }}>
                              {(() => {
                                const gP = get('channels.telegram.groupPolicy', '—');
                                const color = gP === 'open' ? '#22c55e' : gP === 'deny' ? '#ef4444' : gP === 'allowlist' ? '#f59e0b' : undefined;
                                return pill(gP, color);
                              })()}
                            </td>
                            <td style={{ padding: '10px 8px', borderBottom: '1px solid #1a1b2e' }}>
                              {pill(pruneMode, pruneMode === 'cache-ttl' ? '#22c55e' : pruneMode === '—' ? '#f59e0b' : undefined)}
                            </td>
                            <td style={{ padding: '10px 8px', borderBottom: '1px solid #1a1b2e', color: '#c8cbdb' }}>{pruneTtl}</td>
                            <td style={{ padding: '10px 8px', borderBottom: '1px solid #1a1b2e', color: '#c8cbdb' }}>{softTrim !== '—' ? `${Number(softTrim).toLocaleString()}` : '—'}</td>
                            <td style={{ padding: '10px 8px', borderBottom: '1px solid #1a1b2e' }}>
                              {pill(compactMode, compactMode === 'safeguard' ? '#22c55e' : compactMode === '—' ? '#f59e0b' : undefined)}
                            </td>
                            <td style={{ padding: '10px 8px', borderBottom: '1px solid #1a1b2e', color: '#c8cbdb' }}>{reserveFloor !== '—' ? `${Number(reserveFloor).toLocaleString()}` : '—'}</td>
                            <td style={{ padding: '10px 8px', borderBottom: '1px solid #1a1b2e', color: '#c8cbdb' }}>{bootstrap !== '—' ? `${Number(bootstrap).toLocaleString()}` : '—'}</td>
                            <td style={{ padding: '10px 8px', borderBottom: '1px solid #1a1b2e', color: '#c8cbdb' }}>{webFetch !== '—' ? `${Number(webFetch).toLocaleString()}` : '—'}</td>
                            <td style={{ padding: '10px 8px', borderBottom: '1px solid #1a1b2e' }}>
                              {pill(sessMode, sessMode === 'enforce' ? '#22c55e' : sessMode === '—' ? '#f59e0b' : undefined)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Legenda */}
              <div style={{ display: 'flex', gap: 16, fontSize: 11, color: '#6b7084', paddingTop: 4 }}>
                <span>🟢 Configurado</span>
                <span>🟡 Não definido (usando default)</span>
                <span>🔴 Erro ao carregar</span>
                <span style={{ marginLeft: 'auto' }}>Clique em uma linha para editar</span>
              </div>
            </>
          )}

          {/* ── EDITOR: Workspace selecionado ── */}
          {selectedWs.value && (
            <>
              {/* Header com botão voltar */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Button variant="ghost"
                  onClick={() => { selectedWs.value = ''; wsConfig.value = null; configError.value = ''; fetchAllConfigs(); }}
                  style={{ padding: '8px 14px', background: '#1a1b2e', color: '#e0e0e6', border: '1px solid #2d2f45', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
                  ← Voltar
                </Button>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>
                    {workspaces.value.find((w: any) => (w.namespace || `ws-${w.id}`) === selectedWs.value)?.name || selectedWs.value}
                  </div>
                  <div style={{ fontSize: 12, color: '#8b8fa3' }}>{selectedWs.value}</div>
                </div>
              </div>

          {configLoading.value && <div style={{ textAlign: 'center', padding: 20 }}><ListSkeleton rows={3} /></div>}
          {configError.value && (
            <div style={{ background: T.dangerBg, borderRadius: 8, padding: 12, color: colors.red, fontSize: 13 }}>{configError.value}</div>
          )}

          {wsConfig.value && !configLoading.value && (() => {
            const cardStyle = { background: colors.card, borderRadius: 12, padding: 20, border: `1px solid ${colors.cardBorder}` } as const;
            const sectionTitle = (emoji: string, title: string, desc: string) => (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>{emoji}</span> {title}
                </div>
                <div style={{ fontSize: 12, color: '#8b8fa3', marginTop: 4, lineHeight: 1.5 }}>{desc}</div>
              </div>
            );
            const fieldLabel = (label: string, hint?: string) => (
              <div style={{ marginBottom: 6 }}>
                <label style={{ fontSize: 13, color: '#e0e0e6', display: 'block', fontWeight: 600 }}>{label}</label>
                {hint && <span style={{ fontSize: 11, color: '#6b7084', lineHeight: 1.3 }}>{hint}</span>}
              </div>
            );
            const inputStyle = { width: '100%', padding: '10px 12px', background: '#1a1b2e', border: '1px solid #2d2f45', borderRadius: 8, color: '#f0f0f5', fontSize: 13, boxSizing: 'border-box' as const };

            return (
            <>
              {/* Presets */}
              <div style={cardStyle}>
                {sectionTitle('🚀', 'Presets Rápidos', 'Aplique configurações otimizadas com 1 clique. Balanced é o mais recomendado.')}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {Object.entries(PRESETS).map(([key, p]) => (
                    <Button key={key} variant="ghost"
                      onClick={() => applyPreset(key)}
                      style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #2a2b4a, #1e1f38)', border: '1px solid #3d3f5c', borderRadius: 10, color: '#e0e0e6', cursor: 'pointer', fontSize: 13, fontWeight: 600, transition: 'all 0.2s' }}
                    >
                      {p.emoji} {p.label}
                      <div style={{ fontSize: 10, color: '#8b8fa3', marginTop: 2, fontWeight: 400 }}>{p.desc}</div>
                    </Button>
                  ))}
                </div>
                {configDirty.value && <div style={{ fontSize: 12, color: colors.yellow, marginTop: 10, fontWeight: 600 }}>⚠️ Alterações não salvas — clique em Salvar abaixo</div>}
              </div>

              {/* Modelo & Timeout */}
              <div style={cardStyle}>
                {sectionTitle('🤖', 'Modelo & Timeout', 'Define qual modelo a IA usa nos heartbeats (checagens periódicas) e o tempo máximo por turno.')}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    {fieldLabel('Modelo Heartbeat', 'Modelo barato para checagens automáticas (ex: elon/claude-haiku-4-5)')}
                    <input value={getConfigVal('agents.defaults.heartbeat.model', '')}
                      onInput={(e) => updateConfigField('agents.defaults.heartbeat.model', (e.target as HTMLInputElement).value)}
                      placeholder="elon/claude-haiku-4-5"
                      style={inputStyle} />
                  </div>
                  <div>
                    {fieldLabel('Timeout (segundos)', 'Tempo máximo que o agente pode levar por turno. 900 = 15 min')}
                    <input type="number" value={getConfigVal('agents.defaults.timeoutSeconds', 900)}
                      onInput={(e) => updateConfigField('agents.defaults.timeoutSeconds', Number((e.target as HTMLInputElement).value))}
                      style={inputStyle} />
                  </div>
                </div>

                {/* Sandbox Browser Toggle */}
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #2d2f45' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      {fieldLabel('🌐 Sandbox Browser', 'Permite que o agente use o browser (Playwright) para navegar na web. Se desativado, chamadas ao browser falham com timeout.')}
                    </div>
                    <div
                      onClick={() => {
                        const current = getConfigVal('agents.defaults.sandbox.browser.enabled', false);
                        updateConfigField('agents.defaults.sandbox.browser.enabled', !current);
                      }}
                      style={{
                        width: 48, height: 26, borderRadius: 13, cursor: 'pointer',
                        background: getConfigVal('agents.defaults.sandbox.browser.enabled', false) ? '#22c55e' : '#3d3f5c',
                        position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                      }}
                    >
                      <div style={{
                        width: 20, height: 20, borderRadius: 10, background: '#fff',
                        position: 'absolute', top: 3,
                        left: getConfigVal('agents.defaults.sandbox.browser.enabled', false) ? 25 : 3,
                        transition: 'left 0.2s',
                      }} />
                    </div>
                  </div>
                </div>

                {/* Telegram Streaming */}
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #2d2f45' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      {fieldLabel('📡 Telegram Streaming', 'Modo de envio de mensagens no Telegram. "partial" envia "..." enquanto processa (pode gerar fantasmas em timeout). "full" só envia quando completo. "off" desativa.')}
                    </div>
                    <select
                      value={getConfigVal('channels.telegram.streaming', 'partial')}
                      onChange={(e) => updateConfigField('channels.telegram.streaming', (e.target as HTMLSelectElement).value)}
                      style={{ ...inputStyle, width: 140 }}
                    >
                      <option value="partial">partial (…)</option>
                      <option value="full">full</option>
                      <option value="off">off</option>
                    </select>
                  </div>
                </div>

                {/* Heartbeat Intervalo & Active Hours */}
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #2d2f45' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <div>
                      {fieldLabel('Intervalo Heartbeat', 'De quanto em quanto tempo o agente faz check automático')}
                      <select value={getConfigVal('agents.defaults.heartbeat.every', '1h')}
                        onChange={(e) => updateConfigField('agents.defaults.heartbeat.every', (e.target as HTMLSelectElement).value)}
                        style={inputStyle}>
                        <option value="30m">30 minutos</option>
                        <option value="1h">1 hora</option>
                        <option value="2h">2 horas</option>
                        <option value="4h">4 horas</option>
                      </select>
                    </div>
                    <div>
                      {fieldLabel('Timezone', 'Fuso horário para horários ativos')}
                      <select value={getConfigVal('agents.defaults.heartbeat.activeHours.timezone', 'America/Sao_Paulo')}
                        onChange={(e) => updateConfigField('agents.defaults.heartbeat.activeHours.timezone', (e.target as HTMLSelectElement).value)}
                        style={inputStyle}>
                        <option value="America/Sao_Paulo">America/Sao_Paulo</option>
                        <option value="America/New_York">America/New_York</option>
                        <option value="Europe/London">Europe/London</option>
                        <option value="UTC">UTC</option>
                      </select>
                    </div>
                    <div>
                      {fieldLabel('Horário Início', 'Formato HH:MM (ex: 00:00)')}
                      <input value={getConfigVal('agents.defaults.heartbeat.activeHours.start', '')}
                        onInput={(e) => updateConfigField('agents.defaults.heartbeat.activeHours.start', (e.target as HTMLInputElement).value)}
                        placeholder="00:00"
                        style={inputStyle} />
                    </div>
                    <div>
                      {fieldLabel('Horário Fim', 'Formato HH:MM (ex: 23:59)')}
                      <input value={getConfigVal('agents.defaults.heartbeat.activeHours.end', '')}
                        onInput={(e) => updateConfigField('agents.defaults.heartbeat.activeHours.end', (e.target as HTMLInputElement).value)}
                        placeholder="23:59"
                        style={inputStyle} />
                    </div>
                  </div>
                </div>

                {/* Max Concurrent */}
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #2d2f45' }}>
                  <div>
                    {fieldLabel('Max Concurrent', 'Máximo de sessões simultâneas do agente')}
                    <input type="number" value={getConfigVal('agents.defaults.maxConcurrent', '')}
                      onInput={(e) => updateConfigField('agents.defaults.maxConcurrent', Number((e.target as HTMLInputElement).value))}
                      placeholder="3"
                      style={{ ...inputStyle, width: 200 }} />
                  </div>
                </div>
              </div>

              {/* Sub-agentes */}
              <div style={cardStyle}>
                {sectionTitle('🤖', 'Sub-agentes', 'Configurações de sub-agentes spawned pelo agente principal.')}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    {fieldLabel('Max Sub-agentes', 'Máximo de sub-agentes rodando ao mesmo tempo')}
                    <input type="number" value={getConfigVal('agents.defaults.subagents.maxConcurrent', '')}
                      onInput={(e) => updateConfigField('agents.defaults.subagents.maxConcurrent', Number((e.target as HTMLInputElement).value))}
                      placeholder="3"
                      style={inputStyle} />
                  </div>
                  <div>
                    {fieldLabel('Arquivar após (min)', 'Minutos de inatividade antes de arquivar sub-agente')}
                    <input type="number" value={getConfigVal('agents.defaults.subagents.archiveAfterMinutes', '')}
                      onInput={(e) => updateConfigField('agents.defaults.subagents.archiveAfterMinutes', Number((e.target as HTMLInputElement).value))}
                      placeholder="30"
                      style={inputStyle} />
                  </div>
                </div>
              </div>

              {/* Context Pruning */}
              <div style={cardStyle}>
                {sectionTitle('🧹', 'Context Pruning', 'Remove automaticamente resultados antigos de ferramentas para economizar tokens e evitar overflow de contexto.')}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    {fieldLabel('Mode', 'Tipo de limpeza. cache-ttl = remove por tempo')}
                    <select value={getConfigVal('agents.defaults.contextPruning.mode', 'cache-ttl')}
                      onChange={(e) => updateConfigField('agents.defaults.contextPruning.mode', (e.target as HTMLSelectElement).value)}
                      style={inputStyle}>
                      <option value="cache-ttl">cache-ttl</option>
                      <option value="off">off (desativado)</option>
                    </select>
                  </div>
                  <div>
                    {fieldLabel('TTL', 'Tempo de vida dos resultados de tools antes de serem removidos')}
                    <select value={getConfigVal('agents.defaults.contextPruning.ttl', '30m')}
                      onChange={(e) => updateConfigField('agents.defaults.contextPruning.ttl', (e.target as HTMLSelectElement).value)}
                      style={inputStyle}>
                      <option value="15m">15 minutos</option>
                      <option value="30m">30 minutos</option>
                      <option value="45m">45 minutos</option>
                      <option value="60m">60 minutos</option>
                    </select>
                  </div>
                  <div>
                    {fieldLabel('Soft Trim Max Chars', 'Resultados maiores que isso são cortados (mantém início + fim)')}
                    <input type="number" value={getConfigVal('agents.defaults.contextPruning.softTrim.maxChars', 8000)}
                      onInput={(e) => updateConfigField('agents.defaults.contextPruning.softTrim.maxChars', Number((e.target as HTMLInputElement).value))}
                      style={inputStyle} />
                  </div>
                  <div>
                    {fieldLabel('Hard Clear Ratio', 'Quando contexto atinge essa % do limite, remove resultados expirados. 0.85 = 85%')}
                    <input type="number" step="0.05" min="0" max="1" value={getConfigVal('agents.defaults.contextPruning.hardClearRatio', 0.85)}
                      onInput={(e) => updateConfigField('agents.defaults.contextPruning.hardClearRatio', Number((e.target as HTMLInputElement).value))}
                      style={inputStyle} />
                  </div>
                  <div>
                    {fieldLabel('Soft Trim Ratio', 'A partir dessa % do limite, começa a cortar resultados grandes. 0.6 = 60%')}
                    <input type="number" step="0.05" min="0" max="1" value={getConfigVal('agents.defaults.contextPruning.softTrimRatio', 0.6)}
                      onInput={(e) => updateConfigField('agents.defaults.contextPruning.softTrimRatio', Number((e.target as HTMLInputElement).value))}
                      style={inputStyle} />
                  </div>
                </div>
              </div>

              {/* Compaction */}
              <div style={cardStyle}>
                {sectionTitle('📦', 'Compaction', 'Quando o contexto fica grande demais, a IA "resume" a conversa para caber na janela. Isso evita perda total de contexto.')}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    {fieldLabel('Mode', 'safeguard = compacta automaticamente quando necessário')}
                    <select value={getConfigVal('agents.defaults.compaction.mode', 'safeguard')}
                      onChange={(e) => updateConfigField('agents.defaults.compaction.mode', (e.target as HTMLSelectElement).value)}
                      style={inputStyle}>
                      <option value="safeguard">safeguard (automático)</option>
                      <option value="manual">manual</option>
                      <option value="off">off (desativado)</option>
                    </select>
                  </div>
                  <div>
                    {fieldLabel('Reserve Tokens Floor', 'Tokens reservados para a resposta. 40000 = reserva ~40K tokens')}
                    <input type="number" value={getConfigVal('agents.defaults.compaction.reserveTokensFloor', 40000)}
                      onInput={(e) => updateConfigField('agents.defaults.compaction.reserveTokensFloor', Number((e.target as HTMLInputElement).value))}
                      style={inputStyle} />
                  </div>
                  <div>
                    {fieldLabel('Max History Share', 'Quanto do histórico manter após compactar. 0.7 = mantém 70%')}
                    <input type="number" step="0.05" min="0" max="1" value={getConfigVal('agents.defaults.compaction.maxHistoryShare', 0.7)}
                      onInput={(e) => updateConfigField('agents.defaults.compaction.maxHistoryShare', Number((e.target as HTMLInputElement).value))}
                      style={inputStyle} />
                  </div>
                  <div>
                    {fieldLabel('Memory Flush Threshold', 'Salva memória automaticamente quando contexto atinge esse nº de tokens')}
                    <input type="number" value={getConfigVal('agents.defaults.compaction.memoryFlush.softThresholdTokens', 120000)}
                      onInput={(e) => updateConfigField('agents.defaults.compaction.memoryFlush.softThresholdTokens', Number((e.target as HTMLInputElement).value))}
                      style={inputStyle} />
                  </div>
                </div>
              </div>

              {/* Web & Bootstrap */}
              <div style={cardStyle}>
                {sectionTitle('🌐', 'Web & Bootstrap', 'Limita o tamanho de dados que entram no contexto da IA ao iniciar ou buscar páginas web.')}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    {fieldLabel('Bootstrap Max Chars', 'Máximo de caracteres dos arquivos do workspace carregados no system prompt')}
                    <input type="number" value={getConfigVal('agents.defaults.bootstrapMaxChars', 15000)}
                      onInput={(e) => updateConfigField('agents.defaults.bootstrapMaxChars', Number((e.target as HTMLInputElement).value))}
                      style={inputStyle} />
                  </div>
                  <div>
                    {fieldLabel('Web Fetch Max Chars', 'Máximo de caracteres ao buscar uma página web (web_fetch tool)')}
                    <input type="number" value={getConfigVal('tools.web.fetch.maxChars', 30000)}
                      onInput={(e) => updateConfigField('tools.web.fetch.maxChars', Number((e.target as HTMLInputElement).value))}
                      style={inputStyle} />
                  </div>
                </div>
              </div>

              {/* Session Maintenance */}
              <div style={cardStyle}>
                {sectionTitle('📋', 'Session Maintenance', 'Limpeza automática de sessões antigas para não acumular lixo no disco.')}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                  <div>
                    {fieldLabel('Mode', 'enforce = limpa automaticamente')}
                    <select value={getConfigVal('session.maintenance.mode', 'enforce')}
                      onChange={(e) => updateConfigField('session.maintenance.mode', (e.target as HTMLSelectElement).value)}
                      style={inputStyle}>
                      <option value="enforce">enforce (automático)</option>
                      <option value="warn">warn (só avisa)</option>
                      <option value="off">off (desativado)</option>
                    </select>
                  </div>
                  <div>
                    {fieldLabel('Prune Days', 'Sessões inativas há X dias são removidas')}
                    <input type="number" value={getConfigVal('session.maintenance.pruneDays', 7)}
                      onInput={(e) => updateConfigField('session.maintenance.pruneDays', Number((e.target as HTMLInputElement).value))}
                      style={inputStyle} />
                  </div>
                  <div>
                    {fieldLabel('Max Entries', 'Máximo de entradas por sessão antes de limpar')}
                    <input type="number" value={getConfigVal('session.maintenance.maxEntries', 500)}
                      onInput={(e) => updateConfigField('session.maintenance.maxEntries', Number((e.target as HTMLInputElement).value))}
                      style={inputStyle} />
                  </div>
                </div>
              </div>

              {/* Mensagens & Comandos */}
              <div style={cardStyle}>
                {sectionTitle('💬', 'Mensagens & Comandos', 'Configurações de reações em grupo e permissões de comandos.')}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    {fieldLabel('Reações em Grupo', 'Quando reagir com emoji em mensagens de grupo')}
                    <select value={getConfigVal('messages.ackReactionScope', 'all')}
                      onChange={(e) => updateConfigField('messages.ackReactionScope', (e.target as HTMLSelectElement).value)}
                      style={inputStyle}>
                      <option value="all">all</option>
                      <option value="group-mentions">group-mentions</option>
                      <option value="off">off</option>
                    </select>
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
                      <div>{fieldLabel('Bash Commands', 'Permite que o agente execute comandos bash no terminal')}</div>
                      <div
                        onClick={() => {
                          const current = getConfigVal('commands.bash', false);
                          updateConfigField('commands.bash', !current);
                        }}
                        style={{
                          width: 48, height: 26, borderRadius: 13, cursor: 'pointer',
                          background: getConfigVal('commands.bash', false) ? '#22c55e' : '#3d3f5c',
                          position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                        }}
                      >
                        <div style={{
                          width: 20, height: 20, borderRadius: 10, background: '#fff',
                          position: 'absolute', top: 3,
                          left: getConfigVal('commands.bash', false) ? 25 : 3,
                          transition: 'left 0.2s',
                        }} />
                      </div>
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #2d2f45' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <div>
                      {fieldLabel('Comandos Nativos', 'Habilita comandos nativos do OpenClaw (/status, /restart, etc)')}
                      <select value={getConfigVal('commands.native', 'auto')}
                        onChange={(e) => updateConfigField('commands.native', (e.target as HTMLSelectElement).value)}
                        style={inputStyle}>
                        <option value="auto">auto</option>
                        <option value="on">on</option>
                        <option value="off">off</option>
                      </select>
                    </div>
                    <div>
                      {fieldLabel('Skills Nativas', 'Habilita skills nativas do OpenClaw')}
                      <select value={getConfigVal('commands.nativeSkills', 'auto')}
                        onChange={(e) => updateConfigField('commands.nativeSkills', (e.target as HTMLSelectElement).value)}
                        style={inputStyle}>
                        <option value="auto">auto</option>
                        <option value="on">on</option>
                        <option value="off">off</option>
                      </select>
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>{fieldLabel('Comando /restart', 'Permite reiniciar o gateway via comando')}</div>
                        <div
                          onClick={() => {
                            const current = getConfigVal('commands.restart', false);
                            updateConfigField('commands.restart', !current);
                          }}
                          style={{
                            width: 48, height: 26, borderRadius: 13, cursor: 'pointer',
                            background: getConfigVal('commands.restart', false) ? '#22c55e' : '#3d3f5c',
                            position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                          }}
                        >
                          <div style={{
                            width: 20, height: 20, borderRadius: 10, background: '#fff',
                            position: 'absolute', top: 3,
                            left: getConfigVal('commands.restart', false) ? 25 : 3,
                            transition: 'left 0.2s',
                          }} />
                        </div>
                      </div>
                    </div>
                    <div>
                      {fieldLabel('Owner Display', 'Como mostrar informações do dono nas mensagens')}
                      <select value={getConfigVal('commands.ownerDisplay', 'raw')}
                        onChange={(e) => updateConfigField('commands.ownerDisplay', (e.target as HTMLSelectElement).value)}
                        style={inputStyle}>
                        <option value="raw">raw</option>
                        <option value="formatted">formatted</option>
                        <option value="off">off</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Telegram Policies */}
              <div style={cardStyle}>
                {sectionTitle('📡', 'Telegram', 'Políticas de acesso do bot no Telegram.')}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    {fieldLabel('Política de Grupos', 'Quem pode interagir com o bot em grupos. allowlist = só grupos autorizados')}
                    <select value={getConfigVal('channels.telegram.groupPolicy', 'open')}
                      onChange={(e) => updateConfigField('channels.telegram.groupPolicy', (e.target as HTMLSelectElement).value)}
                      style={inputStyle}>
                      <option value="open">open</option>
                      <option value="allowlist">allowlist</option>
                      <option value="deny">deny</option>
                    </select>
                  </div>
                  <div>
                    {fieldLabel('Política de DMs', 'Quem pode enviar DM pro bot. allowlist = só usuários autorizados')}
                    <select value={getConfigVal('channels.telegram.dmPolicy', 'open')}
                      onChange={(e) => updateConfigField('channels.telegram.dmPolicy', (e.target as HTMLSelectElement).value)}
                      style={inputStyle}>
                      <option value="open">open</option>
                      <option value="allowlist">allowlist</option>
                      <option value="deny">deny</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Gateway */}
              <div style={cardStyle}>
                {sectionTitle('🔒', 'Gateway', 'Configurações de autenticação e modo do gateway.')}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>{fieldLabel('Allow Insecure Auth', 'Permite autenticação sem HTTPS na UI de controle (necessário em containers)')}</div>
                      <div
                        onClick={() => {
                          const current = getConfigVal('gateway.controlUi.allowInsecureAuth', false);
                          updateConfigField('gateway.controlUi.allowInsecureAuth', !current);
                        }}
                        style={{
                          width: 48, height: 26, borderRadius: 13, cursor: 'pointer',
                          background: getConfigVal('gateway.controlUi.allowInsecureAuth', false) ? '#22c55e' : '#3d3f5c',
                          position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                        }}
                      >
                        <div style={{
                          width: 20, height: 20, borderRadius: 10, background: '#fff',
                          position: 'absolute', top: 3,
                          left: getConfigVal('gateway.controlUi.allowInsecureAuth', false) ? 25 : 3,
                          transition: 'left 0.2s',
                        }} />
                      </div>
                    </div>
                  </div>
                  <div>
                    {fieldLabel('Auth Mode', 'Modo de autenticação do gateway')}
                    <select value={getConfigVal('gateway.auth.mode', 'token')}
                      onChange={(e) => updateConfigField('gateway.auth.mode', (e.target as HTMLSelectElement).value)}
                      style={inputStyle}>
                      <option value="token">token</option>
                      <option value="none">none</option>
                    </select>
                  </div>
                  <div>
                    {fieldLabel('Gateway Mode', 'Modo de operação do gateway')}
                    <select value={getConfigVal('gateway.mode', 'local')}
                      onChange={(e) => updateConfigField('gateway.mode', (e.target as HTMLSelectElement).value)}
                      style={inputStyle}>
                      <option value="local">local</option>
                      <option value="remote">remote</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Plugins (Canais) */}
              <div style={cardStyle}>
                {sectionTitle('🔌', 'Plugins (Canais)', 'Ativar/desativar canais de comunicação disponíveis.')}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {['telegram', 'whatsapp', 'discord', 'slack', 'signal', 'imessage', 'nostr', 'googlechat'].map(ch => (
                    <div key={ch} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#1a1b2e', borderRadius: 8, border: '1px solid #2d2f45' }}>
                      <span style={{ fontSize: 13, color: '#e0e0e6', fontWeight: 600, textTransform: 'capitalize' }}>{ch}</span>
                      <div
                        onClick={() => {
                          const path = `plugins.entries.${ch}.enabled`;
                          const current = getConfigVal(path, false);
                          updateConfigField(path, !current);
                        }}
                        style={{
                          width: 48, height: 26, borderRadius: 13, cursor: 'pointer',
                          background: getConfigVal(`plugins.entries.${ch}.enabled`, false) ? '#22c55e' : '#3d3f5c',
                          position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                        }}
                      >
                        <div style={{
                          width: 20, height: 20, borderRadius: 10, background: '#fff',
                          position: 'absolute', top: 3,
                          left: getConfigVal(`plugins.entries.${ch}.enabled`, false) ? 25 : 3,
                          transition: 'left 0.2s',
                        }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Browser */}
              <div style={cardStyle}>
                {sectionTitle('🌐', 'Browser', 'Configurações avançadas do browser Playwright.')}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>{fieldLabel('Headless', 'Rodar browser sem interface gráfica (obrigatório em containers)')}</div>
                      <div
                        onClick={() => {
                          const current = getConfigVal('browser.headless', false);
                          updateConfigField('browser.headless', !current);
                        }}
                        style={{
                          width: 48, height: 26, borderRadius: 13, cursor: 'pointer',
                          background: getConfigVal('browser.headless', false) ? '#22c55e' : '#3d3f5c',
                          position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                        }}
                      >
                        <div style={{
                          width: 20, height: 20, borderRadius: 10, background: '#fff',
                          position: 'absolute', top: 3,
                          left: getConfigVal('browser.headless', false) ? 25 : 3,
                          transition: 'left 0.2s',
                        }} />
                      </div>
                    </div>
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>{fieldLabel('No Sandbox', 'Desativar sandbox do Chromium (necessário em containers Docker)')}</div>
                      <div
                        onClick={() => {
                          const current = getConfigVal('browser.noSandbox', false);
                          updateConfigField('browser.noSandbox', !current);
                        }}
                        style={{
                          width: 48, height: 26, borderRadius: 13, cursor: 'pointer',
                          background: getConfigVal('browser.noSandbox', false) ? '#22c55e' : '#3d3f5c',
                          position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                        }}
                      >
                        <div style={{
                          width: 20, height: 20, borderRadius: 10, background: '#fff',
                          position: 'absolute', top: 3,
                          left: getConfigVal('browser.noSandbox', false) ? 25 : 3,
                          transition: 'left 0.2s',
                        }} />
                      </div>
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 16 }}>
                  {fieldLabel('Perfil Padrão', 'Perfil de browser usado por padrão')}
                  <select value={getConfigVal('browser.defaultProfile', 'openclaw')}
                    onChange={(e) => updateConfigField('browser.defaultProfile', (e.target as HTMLSelectElement).value)}
                    style={{ ...inputStyle, width: 200 }}>
                    <option value="openclaw">openclaw</option>
                    <option value="chrome">chrome</option>
                  </select>
                </div>
              </div>

              {/* Botões Salvar / Recarregar */}
              <div style={{ display: 'flex', gap: 10, padding: '4px 0' }}>
                <Button variant="ghost" onClick={() => saveConfig(true)} disabled={configSaving.value}
                  style={{ padding: '12px 24px', background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 14, opacity: configSaving.value ? 0.5 : 1 }}>
                  {configSaving.value ? '⏳ Salvando...' : '💾 Salvar + Restart'}
                </Button>
                <Button variant="ghost" onClick={() => saveConfig(false)} disabled={configSaving.value}
                  style={{ padding: '12px 24px', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 14, opacity: configSaving.value ? 0.5 : 1 }}>
                  💾 Salvar (sem restart)
                </Button>
                <Button variant="ghost" onClick={() => fetchConfig(selectedWs.value)}
                  style={{ padding: '12px 24px', background: '#1a1b2e', color: '#e0e0e6', border: '1px solid #2d2f45', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                  🔄 Recarregar
                </Button>
              </div>

              {/* JSON Raw */}
              <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
                <div onClick={() => showRawJson.value = !showRawJson.value}
                  style={{ padding: '16px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>📝 Config JSON Raw</div>
                  <span style={{ color: '#8b8fa3', fontSize: 12 }}>{showRawJson.value ? '▲ Fechar' : '▼ Expandir'}</span>
                </div>
                {showRawJson.value && (
                  <div style={{ padding: '0 20px 20px' }}>
                    <textarea
                      value={configJsonRaw.value}
                      onInput={(e) => {
                        const val = (e.target as HTMLTextAreaElement).value;
                        configJsonRaw.value = val;
                        try {
                          wsConfig.value = JSON.parse(val);
                          configDirty.value = true;
                          configError.value = '';
                        } catch { configError.value = 'JSON inválido'; }
                      }}
                      rows={20}
                      style={{ width: '100%', padding: '12px 14px', background: '#0d0e1a', border: '1px solid #2d2f45', borderRadius: 8, color: '#c8cbdb', fontSize: 12, fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box' }}
                    />
                  </div>
                )}
              </div>
            </>
            );
          })()}
          </>
          )}
        </div>
      )}

      {/* ── Seção Histórico ── */}
      {section.value === 'history' && (
        <div>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: colors.text, marginBottom: 16, margin: '0 0 16px 0' }}>Histórico de Versões</h3>
          
          {history.value.length === 0 && (
            <div style={{ background: colors.card, borderRadius: 12, padding: 40, border: `1px solid ${colors.cardBorder}`, textAlign: 'center', color: colors.textMuted }}>
              Nenhuma mudança registrada
            </div>
          )}

          {history.value.length > 0 && (
            <div style={{ background: colors.card, borderRadius: 12, border: `1px solid ${colors.cardBorder}`, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
                    {['Versão', 'Anterior', 'Data', 'Quem', 'Notas'].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: colors.textMuted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase' as const }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {history.value.map((h: any, i: number) => (
                    <tr key={h.id || i} style={{ borderBottom: i < history.value.length - 1 ? `1px solid ${colors.cardBorder}` : 'none' }}>
                      <td style={{ padding: '10px 16px', color: colors.green, fontWeight: 600, fontFamily: 'monospace' }}>{h.version}</td>
                      <td style={{ padding: '10px 16px', color: colors.textMuted, fontFamily: 'monospace' }}>{h.previous_version || '—'}</td>
                      <td style={{ padding: '10px 16px', color: colors.text }}>{h.applied_at ? new Date(h.applied_at + 'Z').toLocaleString('pt-BR') : '—'}</td>
                      <td style={{ padding: '10px 16px', color: colors.textMuted }}>{h.applied_by || '—'}</td>
                      <td style={{ padding: '10px 16px', color: colors.textMuted }}>{h.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Modal: Aplicar Versão ── */}
      {showVersionModal.value && (
        <div style={{ position: 'fixed', inset: 0, background: T.bgOverlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
          onClick={(e) => { if (e.target === e.currentTarget) showVersionModal.value = false; }}>
          <div style={{ background: colors.card, borderRadius: 16, padding: 24, width: 400, maxWidth: '90vw', border: `1px solid ${colors.cardBorder}` }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: colors.text, marginBottom: 16, margin: '0 0 16px 0' }}>Aplicar Nova Versão</h3>
            
            <label style={{ display: 'block', fontSize: 13, color: colors.textMuted, marginBottom: 6 }}>Versão</label>
            <input
              value={newVersion.value}
              onInput={(e) => newVersion.value = (e.target as HTMLInputElement).value}
              placeholder="ex: 0.5.2 ou latest"
              style={{ width: '100%', padding: '10px 12px', background: colors.bg, border: `1px solid ${colors.cardBorder}`, borderRadius: 8, color: colors.text, fontSize: 14, marginBottom: 12, boxSizing: 'border-box' }}
            />
            
            <label style={{ display: 'block', fontSize: 13, color: colors.textMuted, marginBottom: 6 }}>Notas (opcional)</label>
            <Textarea
              value={versionNotes.value}
              onInput={(e) => versionNotes.value = (e.target as HTMLTextAreaElement).value}
              placeholder="Motivo da atualização..."
              rows={3}
              style={{ width: '100%', padding: '10px 12px', background: colors.bg, border: `1px solid ${colors.cardBorder}`, borderRadius: 8, color: colors.text, fontSize: 14, marginBottom: 16, resize: 'vertical', boxSizing: 'border-box' }}
            />
            
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={() => showVersionModal.value = false}
                style={{ padding: '8px 16px', background: 'transparent', color: colors.textMuted, border: `1px solid ${colors.cardBorder}`, borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
                Cancelar
              </Button>
              <Button variant="ghost" onClick={applyVersion} disabled={actionLoading.value}
                style={{ padding: '8px 16px', background: colors.blue, color: T.text, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13, opacity: actionLoading.value ? 0.5 : 1 }}>
                {actionLoading.value ? 'Aplicando...' : 'Aplicar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Criar Template ── */}
      {showTemplateModal.value && (
        <div style={{ position: 'fixed', inset: 0, background: T.bgOverlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
          onClick={(e) => { if (e.target === e.currentTarget) showTemplateModal.value = false; }}>
          <div style={{ background: colors.card, borderRadius: 16, padding: 24, width: 400, maxWidth: '90vw', border: `1px solid ${colors.cardBorder}` }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: colors.text, marginBottom: 16, margin: '0 0 16px 0' }}>Criar Template</h3>
            
            <label style={{ display: 'block', fontSize: 13, color: colors.textMuted, marginBottom: 6 }}>Nome *</label>
            <input
              value={templateName.value}
              onInput={(e) => templateName.value = (e.target as HTMLInputElement).value}
              placeholder="ex: Template Padrão"
              style={{ width: '100%', padding: '10px 12px', background: colors.bg, border: `1px solid ${colors.cardBorder}`, borderRadius: 8, color: colors.text, fontSize: 14, marginBottom: 12, boxSizing: 'border-box' }}
            />
            
            <label style={{ display: 'block', fontSize: 13, color: colors.textMuted, marginBottom: 6 }}>Descrição</label>
            <input
              value={templateDesc.value}
              onInput={(e) => templateDesc.value = (e.target as HTMLInputElement).value}
              placeholder="Descrição opcional"
              style={{ width: '100%', padding: '10px 12px', background: colors.bg, border: `1px solid ${colors.cardBorder}`, borderRadius: 8, color: colors.text, fontSize: 14, marginBottom: 12, boxSizing: 'border-box' }}
            />
            
            <label style={{ display: 'block', fontSize: 13, color: colors.textMuted, marginBottom: 6 }}>ID do Workspace *</label>
            <input
              value={templateWsId.value}
              onInput={(e) => templateWsId.value = (e.target as HTMLInputElement).value}
              placeholder="ex: abc123"
              style={{ width: '100%', padding: '10px 12px', background: colors.bg, border: `1px solid ${colors.cardBorder}`, borderRadius: 8, color: colors.text, fontSize: 14, marginBottom: 16, boxSizing: 'border-box' }}
            />
            
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={() => showTemplateModal.value = false}
                style={{ padding: '8px 16px', background: 'transparent', color: colors.textMuted, border: `1px solid ${colors.cardBorder}`, borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
                Cancelar
              </Button>
              <Button variant="ghost" onClick={createTemplate} disabled={actionLoading.value}
                style={{ padding: '8px 16px', background: colors.green, color: T.text, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13, opacity: actionLoading.value ? 0.5 : 1 }}>
                {actionLoading.value ? 'Criando...' : 'Criar Template'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
